import { Anchor, Avatar, Box, Flex, Group, Image, Text } from '@mantine/core';
import { IconCheck, IconChecks, IconFile } from '@tabler/icons';
import React from 'react';
import { Message } from '../../../models/chat';
import { formatLocale } from '../../../providers/dayjs-plugins';
import { getFullNameInitials } from '../../../utils/get-full-name-initials';
import { messageBubbleSx, messageContainerSx } from '../styles';
import { EMOJI_ONLY_RE, EMOJI_PART_RE, EMOJI_RE } from '../utils';

interface MessageBubbleProps {
  msg: Message;
  isMe: boolean;
  onPreviewImage?: (url: string) => void;
}

// --- Hoisted style objects ---
// Defined outside the component so they are never re-created on render.
const rowSx = { flexDirection: 'row' as const, width: '100%' };
const rowReverseSx = { flexDirection: 'row-reverse' as const, width: '100%' };
const readReceiptBoxSx = { display: 'flex' };
const metaGroupSx = { alignSelf: 'flex-end', marginBottom: '-2px' };
const timestampSx = { fontSize: '10px' };
const fileAnchorSx = { display: 'flex', alignItems: 'center', gap: 4 };
const senderNameSx = { fontSize: '11px' };
const attachmentImageSx = { maxWidth: 220, cursor: 'pointer' };

function renderMessageContent(content: string) {
  if (EMOJI_ONLY_RE.test(content.trim())) {
    return <span style={{ fontSize: '2rem' }}>{content}</span>;
  }
  return content.split(EMOJI_RE).map((part, i) =>
    EMOJI_PART_RE.test(part) ? (
      <span key={i} style={{ fontSize: '1.25em', verticalAlign: 'middle' }}>
        {part}
      </span>
    ) : (
      part
    ),
  );
}

export const MessageBubble = React.memo(function MessageBubble({
  msg,
  isMe,
  onPreviewImage,
}: MessageBubbleProps) {
  const isRead = !!msg.readAt;
  const isImage = msg.attachmentType?.startsWith('image/');

  return (
    <Box sx={messageContainerSx(isMe)}>
      <Group
        spacing="xs"
        noWrap
        align="flex-end"
        sx={isMe ? rowReverseSx : rowSx}
      >
        {!isMe ? (
          <Avatar
            src={msg.sender?.avatarUrl}
            size="sm"
            radius="xl"
            color="blue"
          >
            {!msg.sender?.avatarUrl &&
              getFullNameInitials(
                msg.username || msg.sender?.username || 'Usuario',
              )}
          </Avatar>
        ) : (
          ''
        )}

        <Box sx={messageBubbleSx(isMe)}>
          {!isMe && (
            <Text
              size="xs"
              weight={500}
              color="dimmed"
              mb={2}
              sx={senderNameSx}
            >
              {msg.username || msg.sender?.username || 'Usuário'}
            </Text>
          )}

          {msg.attachmentUrl && (
            <Box mb={msg.content ? 4 : 0}>
              {isImage ? (
                <Image
                  src={msg.attachmentUrl}
                  radius="md"
                  withPlaceholder
                  sx={attachmentImageSx}
                  onClick={() => {
                    if (onPreviewImage && msg.attachmentUrl) {
                      onPreviewImage(msg.attachmentUrl);
                    } else {
                      window.open(msg.attachmentUrl ?? '', '_blank');
                    }
                  }}
                />
              ) : (
                <Anchor
                  href={msg.attachmentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={fileAnchorSx}
                >
                  <IconFile size={16} />
                  <Text size="xs" lineClamp={1}>
                    {msg.attachmentName || 'Arquivo'}
                  </Text>
                </Anchor>
              )}
            </Box>
          )}

          <Flex justify="space-between" align="center" gap="sm">
            {msg.content && (
              <Text size="md" sx={{ lineHeight: 1.4, fontSize: '14px' }}>
                {renderMessageContent(msg.content)}
              </Text>
            )}
            <Group spacing={4} noWrap align="center" sx={metaGroupSx}>
              <Text size="xs" color="dimmed" sx={timestampSx}>
                {formatLocale(msg.createdAt, 'HH:mm')}
              </Text>
              {isMe && (
                <Box sx={readReceiptBoxSx}>
                  {isRead ? (
                    <IconChecks size={14} color="#34B7F1" />
                  ) : (
                    <IconCheck size={14} color="#999" />
                  )}
                </Box>
              )}
            </Group>
          </Flex>
        </Box>
      </Group>
    </Box>
  );
});
